import classNames from 'classnames';
import React, { FC, useState } from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { RowButton } from '@eika/button';
import { getDateDisplayValue } from '@eika/calendar';
import { Icon, IconTypes } from '@eika/icon';
import { InfoLabel, WarningLabel } from '@eika/label';

import { useBankingHolidays } from '../../../../fetchHooks/useBankingDays';
import type { PaymentInterval } from '../../../../generated-types';
import useTranslation, { format as formatTranslate } from '../../../../hooks/useTranslation';
import { getNotifications } from '../../../shared/DateField';
import { getWarningMessage } from '../../../shared/DateField/utils';
import DateSelectModal from './DateSelectModal';
import EndDateModal from './EndDateModal';
import PaymentIntervalModal from './PaymentIntervalModal';

import './DateSelect.scss';

export interface DateSelectProps {
    className?: string;
    date?: ZonedDate;
    locked?: boolean;
    onChange: (date?: ZonedDate) => void;
    error?: string;
    onlyBusinessDays?: boolean;
    immediate?: boolean;
    onChangeImmediate?: (immediate: boolean) => void;
    warning?: string;
    minDate?: Date;
    paymentInterval?: PaymentInterval;
    onChangePaymentInterval?: (interval?: PaymentInterval) => void;
    endDate?: ZonedDate;
    onSetEndDate?: (date?: ZonedDate) => void;
    label?: string;
    customDateFormat?: string;
    hideInstant?: boolean;
    useFocusModal?: boolean;
}

const today = getZonedDate();

const DateSelect: FC<DateSelectProps> = ({
    className,
    date,
    onChange,
    error,
    locked = false,
    onlyBusinessDays = false,
    immediate,
    onChangeImmediate,
    warning,
    minDate,
    paymentInterval,
    onChangePaymentInterval,
    endDate,
    onSetEndDate,
    label,
    customDateFormat,
    hideInstant = false,
    useFocusModal = false,
}) => {
    const calendarState = useState(false);
    const [showCalendar, setShowCalendar] = calendarState;

    const [showPaymentInterval, setShowPaymentInterval] = useState(false);
    const [showEndDate, setShowEndDate] = useState(false);

    const translated = useTranslation();
    const [spanDates, setSpanDates] = useState<ZonedDate[]>([getZonedDate(), getZonedDate()]);
    const { data: bankHolidayDates } = useBankingHolidays(spanDates[0], spanDates[1], {
        select: (days: ZonedDate[]) => days,
    });

    const notification = getNotifications(translated, onlyBusinessDays, bankHolidayDates);

    const warningMessage = warning || error || getWarningMessage(notification, date);

    const targetMinDate = minDate ? getZonedDate(minDate) : today;

    return (
        <section className={className}>
            <div
                className={classNames('calendar-container', {
                    active: showCalendar,
                })}
            >
                <label className="calendar-container__label">
                    <span className="date-field__label">{label ?? translated.date}</span>
                    <button
                        type="button"
                        onClick={() => setShowCalendar(!showCalendar)}
                        className={classNames('date-field__button ', {
                            active: showCalendar,
                            locked,
                            'focus-modal': useFocusModal,
                        })}
                        disabled={locked}
                        onKeyDown={(e) => {
                            if (showCalendar) {
                                e.stopPropagation();
                            }
                            if (e.key === 'Esc' || e.key === 'Escape') {
                                setShowCalendar(false);
                            }
                        }}
                        data-testid="date-picker-button"
                    >
                        <div className="date-field__layout">
                            <span className="date-field__value">
                                {immediate
                                    ? translated.immediatePayment.title
                                    : getDateDisplayValue(date, customDateFormat)}
                                {paymentInterval && !immediate && (
                                    <span className="date-field__payment-interval">
                                        {translated.dateField.recurringPaymentDateInterval[paymentInterval]}
                                    </span>
                                )}
                            </span>
                            {locked ? (
                                <Icon type={IconTypes.PIN_KODE_24} className="icon--24" ariaLabel="Låst" />
                            ) : (
                                <Icon type={IconTypes.CALENDAR_20} className="icon--20" ariaLabel="Endre" />
                            )}
                        </div>
                    </button>
                </label>
                <DateSelectModal
                    date={date}
                    onChange={onChange}
                    immediate={immediate}
                    onChangeImmediate={onChangeImmediate}
                    error={error}
                    minDate={targetMinDate}
                    calendarState={calendarState}
                    useFocusModal={useFocusModal}
                    hideInstant={hideInstant}
                    setSpanDates={setSpanDates}
                    bankHolidayDates={bankHolidayDates}
                    notification={notification}
                >
                    {onChangePaymentInterval && (
                        <div>
                            <RowButton onClick={() => setShowPaymentInterval(true)}>
                                <RowButton.AccountSelectorLayout
                                    accountAlias={translated.dateField.paymentInterval.alias}
                                    amount={
                                        paymentInterval
                                            ? translated.dateField.recurringPaymentInterval[paymentInterval]
                                            : ''
                                    }
                                    icon={IconTypes.LITEN_PIL_HOEYRE}
                                />
                            </RowButton>
                            {onSetEndDate && (
                                <RowButton className="date-field__end-date-button" onClick={() => setShowEndDate(true)}>
                                    <RowButton.AccountSelectorLayout
                                        accountAlias={translated.dateField.paymentInterval.lastPaymentDate}
                                        amount={
                                            endDate
                                                ? getDateDisplayValue(endDate)
                                                : translated.dateField.paymentInterval.noLastPaymentDate
                                        }
                                        icon={IconTypes.LITEN_PIL_HOEYRE}
                                    />
                                </RowButton>
                            )}
                        </div>
                    )}
                    <EndDateModal
                        date={endDate}
                        onChange={onSetEndDate}
                        minDate={date}
                        open={showEndDate}
                        setOpen={setShowEndDate}
                    />
                    <PaymentIntervalModal
                        open={showPaymentInterval}
                        onChange={onChangePaymentInterval}
                        paymentInterval={paymentInterval}
                        setOpen={setShowPaymentInterval}
                    />
                </DateSelectModal>
            </div>

            {endDate && (
                <InfoLabel
                    className="date-field__payment-interval-label"
                    text={formatTranslate(translated.recurringPayments.endDate, getDateDisplayValue(endDate))}
                />
            )}
            {!!warningMessage && !immediate && !showCalendar && (
                <WarningLabel className="calendar--warning" text={warningMessage} />
            )}
        </section>
    );
};

export default DateSelect;
